import React, { useContext, useEffect, useState, useCallback } from "react";
import Select from "react-select";
import { ButtonGroup, Button } from "aible";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faArrowDown,
  faArrowCircleUp,
  faArrowCircleDown,
  faEye,
  faCog,
  faThumbsUp,
  faThumbsDown,
  faCheckCircle,
  faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";

import SelectedFeatureChart from "components/FeatureChart/SelectedFeatureChart";
import FeatureChartContainer from "containers/FeatureChart/FeatureChart";
import PartialDepPlotContainer from "containers/RunPlot/PartialDepPlot";
import DriverGraphContainer from "containers/RunPlot/DriverGraph";
import WizardModelContainer from "containers/Wizard/WizardModel";

import LeakageChartContainer from "containers/Next/Charts/LeakageChart";
import NextChartContainer from "components/Next/Charts/NextChart";
import NextSearchContainer from "containers/Next/NextSearch";
import LookerSettingsModalContainer from "containers/LookerSettings/LookerSettingsModal";

import ChooseProcessLimitFrontierContainer from "containers/CreateWizard/Constraints/ChooseProcessLimitFrontier";

import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { LineInput } from "components/Inputs/LineInput";

import CapSlider from "components/Project/CapSlider";

import BullseyeBlue from "images/bullseye-arrow-light-blue.svg";
import BullseyeLight from "images/bullseye-arrow-light.svg";
import AcademicBlue from "images/academic-blu.svg";
import ThumbsUp from "images/tup.svg";
import ThumbsDown from "images/tdown.svg";
import SettingsIcon from "images/settings.svg";

import { STEP_TYPE, WIZARD_SECTIONS, ORIGINAL_STEPS } from "containers/Wizard/reducer";
import { PROJECT_MODES } from "containers/Run/reducer";

import { isEnabled } from "utils/featureFlags";

import PinnedIcon from "images/Sense-Icons/pinned.svg";
import UnpinnedIcon from "images/Sense-Icons/unpinned.svg";

import Logo_Aible_Anywhere from "images/Aible_Logo/Aible_Anywhere.svg";

import styles from "./style.module.css";

export default class TableauExplore extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      searchColumn: undefined,
      dashCount: 0
    };

    if (window.tableau) {
      const markSelection = window.tableau.TableauEventType.MarkSelectionChanged;

      window.tableau.extensions.initializeDialogAsync().then(() => {
        const dashboard = window.tableau.extensions.dashboardContent.dashboard;

        for (const worksheet of dashboard.worksheets) {
          const unregisterEventHandlerFunction = worksheet.addEventListener(markSelection, (selectionEvent) => {
            worksheet.getSelectedMarksAsync().then((marks) => {
              let columnName = undefined;
              let valueName = undefined;

              if (marks?.data?.[0]?._data?.[0]) {
                marks?.data?.[0]?._data?.[0].forEach((tValue) => {
                  const testValue = tValue?._value;

                  Object.keys(props.dataset.fields).forEach((pKey) => {
                    props.dataset.fields[pKey].data.forEach((fData) => {
                      if (fData.value.toLowerCase() === testValue) {
                        columnName = pKey;
                        valueName = fData.value;
                      }
                    });
                  });
                });
              }

              if (columnName === undefined) {
                marks?.data?.[0]?._columns &&
                  marks?.data?.[0]?._columns.map((c) => {
                    if (c._fieldName && props.dataset.fields[c._fieldName.replace(" (bin)", "")] !== undefined) {
                      columnName = c._fieldName.replace(" (bin)", "");
                    }
                  });
              }

              this.setState({ searchColumn: columnName });

              if (columnName !== undefined) {
                props.handleSearchInsights(
                  valueName !== undefined
                    ? [
                        {
                          filterField: columnName,
                          filterValue: valueName,
                          label: columnName + ": " + valueName,
                          value: columnName + ": " + valueName
                        }
                      ]
                    : [
                        {
                          field: columnName,
                          label: columnName,
                          value: columnName,
                          mScore: 10000.007704311594,
                          oScore: 1,
                          scaleScore: 0.003493721127019815,
                          score: 6
                        }
                      ]
                );
              } else {
                props.handleSearchInsights([]);
              }
            });
          });
        }
      });
    }
  }

  renderSummary() {
    return (
      <div className={styles.drivers}>
        <div className={styles.dHold} style={{ height: "calc(100% - 90px)" }}></div>
      </div>
    );
  }

  renderGoodChart() {
    const { searchColumn } = this.state;

    return (
      <div className={styles.goodBox} style={{ background: "rgba(220, 245, 235, 1)" }}>
        <div className={styles.gIcon}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#34D399" }} />
        </div>
        <div>
          <div className={styles.wBold}>{"This is a good chart to keep"}</div>
          {"A strong relationship exists between " + searchColumn + " and all other variables"}
        </div>
      </div>
    );
  }

  renderBadChart() {
    return (
      <div className={styles.goodBox} style={{ background: "rgba(248, 230, 194, 1)" }}>
        <div className={styles.gIcon}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </div>
        <div>
          <div className={styles.wBold}>{"This may be a misleading chart"}</div>
          {"Charts where a variable with low quality is in the horizontal axis " +
            "may be statistically less informative and might mislead you."}
        </div>
      </div>
    );
  }

  renderDashboard() {
    const { lookerCount } = this.props;

    const DASHBOARD_ID = 5;

    return (
      <div className={styles.dashboard} key={lookerCount}>
        <iframe
          src={"https://aible.cloud.looker.com/embed/dashboards/" + DASHBOARD_ID}
          style={{ width: "100%", height: "100%", display: "block" }}
        ></iframe>
      </div>
    );
  }

  renderLookerLoading() {
    const { isLookerLoading, dataset, nextSearchInsights, lookerDashboardElementCount } = this.props;
    const insightCount = nextSearchInsights && nextSearchInsights.length > 0 ? nextSearchInsights.length : 20;

    return (
      <div className={styles.lookerLoading}>
        {isLookerLoading ? (
          <div className={styles.lookerLoadMsg}>
            {"Building Dashboard..."}
            <LoadingIndicator style={{ margin: "0px" }} />
          </div>
        ) : (
          <div className={styles.lookerLoadMsg}>
            {"Aible automatically sent " +
              (lookerDashboardElementCount === 0 ? "..." : lookerDashboardElementCount) +
              " insights about what impacts " +
              dataset?.outcome +
              " to your Looker Dashboard."}
          </div>
        )}
      </div>
    );
  }

  render() {
    const {
      dataset,
      createDashboard,
      handleSearchInsights,
      nextSearchInsights,
      updateTableauDataId,
      tableauDataId,
      nextTableauDataId,
      isLookerLoading,
      nextId,
      history,
      updateIsOpenLookerSettings,
      lookerSDK,
      pinnedHexList
    } = this.props;
    const { searchColumn } = this.state;

    const searchScores = dataset?.interaction_scores_lookup?.[searchColumn] ?? {};

    const goodScore = Object.keys(searchScores)
      .filter((fKey) => fKey != searchScores)
      .map((fKey) => searchScores[fKey])
      .reduce((x, y) => x + y, 0);

    const isGood = goodScore > 0.1;

    const isTableauData = tableauDataId !== undefined && tableauDataId === nextTableauDataId;

    return (
      <div className={styles.tHolder}>
        <LookerSettingsModalContainer />
        <div className={styles.tableauExplore}>
          <div className={styles.topHeader}>
            {lookerSDK !== undefined && (
              <div className={styles.lookerControls}>
                <Button onClick={() => createDashboard()} style={{ padding: "5px" }}>
                  {"Create new Dashboard with " +
                    (nextSearchInsights !== undefined && nextSearchInsights.length > 0
                      ? nextSearchInsights.length
                      : pinnedHexList !== undefined
                        ? pinnedHexList.length
                        : 0) +
                    " Insights"}
                </Button>
              </div>
            )}
            <div className={styles.homeLogo}></div>
            <div
              style={{
                marginRight: "10px",
                maxWidth: "250px",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              <img src={BullseyeLight} style={{ height: "20px", width: "20px", marginRight: "3px" }} />
              <span
                style={{
                  fontWeight: "bold",
                  marginLeft: "3px",
                  whiteSpace: "nowrap"
                }}
              >
                {"(" + dataset?.id + ") " + dataset?.outcome}
              </span>
            </div>
            <Button
              style={{ padding: "3px", marginRight: "5px" }}
              onClick={() => updateIsOpenLookerSettings(true)}
              type="lightPurple"
            >
              <FontAwesomeIcon icon={faCog} />
            </Button>
          </div>
          <NextSearchContainer
            id={nextId}
            datasetId={dataset?.id}
            isOnlySearch
            updateSearchColumnState={(s) => this.setState({ searchColumn: s })}
          />
          <div className={styles.exploreList}>
            {this.renderLookerLoading()}
            {searchColumn === undefined ? <div /> : isGood ? this.renderGoodChart() : this.renderBadChart()}
            <div className={styles.mHolder}>
              <div className={styles.muscleChart} style={nextSearchInsights ? { pointerEvents: "none" } : {}}>
                <LeakageChartContainer id={nextId} isTableau />
              </div>
            </div>
            {nextSearchInsights && (
              <div style={{ marginTop: "50px" }}>
                {nextSearchInsights
                  .sort((a, b) =>
                    a?.metadata?.type === "ModelDriver" ? -1 : b?.metadata?.type === "ModelDriver" ? 1 : 0
                  )
                  .map((node, i) => (
                    <div className={styles.nextChart} style={i % 2 !== 0 ? { background: "#f2f3f7" } : {}}>
                      <NextChartContainer
                        id={nextId}
                        // TODO: Madness please fix this. To show muscle chart
                        node={{
                          chart: {
                            chart: node,
                            fields: node.fields,
                            narrative: node?.narrative
                          },
                          fields: node.fields
                        }}
                        chart={{ ...node, chart: node.chart }}
                        isReport={true}
                        showTooltip={true}
                        showLeakage={true}
                        isTableauMode={true}
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

/*
function TestButton() {
  const [isSending, setIsSending, context, setContext] = useState(false);
  const { core40SDK } = useContext(ExtensionContext40);

  const sendRequest = useCallback(() => {
    console.log("what is core", core40SDK);

    createDashboard(core40SDK);
  }, []);

  return (
    <Button disabled={isSending} onClick={sendRequest}>
      {"HelpDash"}
    </Button>
  );
}

function createDashboardElement(
  sdk,
  dashboardId,
  datasetName,
  column,
  outcome
) {
  const queryN = {
    //connection_name: "demo_data",
    model_name: "bigquery_test",
    vis_config: { first: 1, second: "two", type: "looker_column" },
    sql:
      "SELECT dataset." +
      column +
      ", " +
      "AVG(CAST(dataset." +
      outcome +
      " AS FLOAT64)) " +
      "FROM `demo_data." +
      datasetName +
      "` AS dataset " +
      "GROUP BY 1 ORDER BY 1 LIMIT 50",
  };

  sdk.ok(
    sdk.create_sql_query(queryN).then((queryResponse) => {
      const dashReq = {
        body: {
          result_maker_id: queryResponse.value.result_maker_id + "",
          type: "vis",
          title: "Test Title",
          dashboard_id: dashboardId,
        },
      };

      sdk.ok(sdk.create_dashboard_element(dashReq)).then((r) => console.log(r));
    })
  );
}

function createDashboard(sdk) {
  const dashboardId = "1";
  const datasetName = "Bank Loan Fairness Demo";
  const column = ["Product", "Ethnicity", "Marital Status", "Gender"];
  const outcome = "Balance";

  console.log("create dashboard?");

  column.forEach((c) =>
    createDashboardElement(sdk, dashboardId, datasetName, column, outcome)
  );
}
*/
